import React from "react"
import { CommandAction } from "@/contexts/command-palette-context"
import { useGlobalDialogStore } from "@/stores/use-global-dialog-store"

import { ClientForm } from "@/components/clients/use-create-client-dialog"
import { ContactForm } from "@/components/contacts/use-create-contact-dialog"
import { ProspectForm } from "@/components/prospects/use-create-prospect-dialog"

import { isEmail, isName, isPhoneNumber } from "./utils"

export function useNoResultsActions(
  hasSearchQuery: boolean,
  hasResults: boolean,
  isSearching: boolean,
  searchQuery: string
): CommandAction[] {
  return React.useMemo((): CommandAction[] => {
    if (!hasSearchQuery || hasResults || isSearching) return []

    const query = searchQuery.trim()
    const { openDialog, closeDialog } = useGlobalDialogStore.getState()

    const actions: CommandAction[] = []

    const attrs = {
      defaultEmail: isEmail(query) ? query : undefined,
      defaultPhone: isPhoneNumber(query) ? query : undefined,
      defaultFirstName: isName(query) ? query.split(" ")[0] : undefined,
      defaultLastName: isName(query) ? query.split(" ").slice(-1)[0] : undefined,
    }

    actions.push({
      id: "no-results-create-client",
      label: `Create client "${query}"`,
      type: "task" as const,
      keywords: [],
      footerText: "Create new client",
      priority: 300,
      action: () => {
        closeDialog()
        openDialog({
          variant: "custom",
          content: <ClientForm {...attrs} />,
          dialogContentClasses: "max-w-(--breakpoint-sm)",
        })
      },
    })

    actions.push({
      id: "no-results-create-prospect",
      label: `Create prospect "${query}"`,
      type: "task" as const,
      keywords: [],
      footerText: "Create new prospect",
      priority: 198,
      action: () => {
        closeDialog()
        openDialog({
          variant: "custom",
          content: <ProspectForm {...attrs} />,
          dialogContentClasses: "max-w-(--breakpoint-sm)",
        })
      },
    })

    actions.push({
      id: "no-results-create-contact",
      label: `Create contact "${query}"`,
      type: "task" as const,
      keywords: [],
      footerText: "Create new contact",
      priority: 197,
      action: () => {
        closeDialog()
        openDialog({
          variant: "custom",
          content: <ContactForm {...attrs} />,
          dialogContentClasses: "max-w-(--breakpoint-sm)",
        })
      },
    })

    return actions
  }, [hasSearchQuery, hasResults, isSearching, searchQuery])
}
